import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFavorites } from "../../contexts/FavoritesContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { ApiShirt } from "../../types/Shirt";
import type { Auction } from "../../services/auctionService";
import type { ImageUrl } from "../../types/common";

import type { AuctionBidStatus } from "../../hooks/useAuctionBidStatuses";

interface AuctionShirtCardProps {
  idx?: number;
  hoursLeft?: number;
  currentBid?: number;
  shirt?: ApiShirt;
  auction?: Auction;
  bidStatus?: AuctionBidStatus;
}

function computeTimeLeftInfo(endTime: string): { label: string; urgent: boolean } {
  const diff = new Date(endTime).getTime() - Date.now();
  if (diff <= 0) return { label: "Afgelopen", urgent: false };
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const urgent = diff < 86_400_000;
  if (days > 0) return { label: `${days}d ${hours}h ${mins}m`, urgent };
  if (hours > 0) return { label: `${hours}h ${mins}m`, urgent };
  return { label: `${mins}m`, urgent };
}

function useTimeLeft(endTime?: string | null) {
  const [state, setState] = useState(() =>
    endTime ? computeTimeLeftInfo(endTime) : { label: "", urgent: false },
  );

  useEffect(() => {
    if (!endTime) { setState({ label: "", urgent: false }); return; }
    setState(computeTimeLeftInfo(endTime));
    const interval = setInterval(() => setState(computeTimeLeftInfo(endTime)), 60_000);
    return () => clearInterval(interval);
  }, [endTime]);

  return state;
}

function AuctionShirtCardInner({
  idx = 0,
  hoursLeft = 7,
  currentBid = 100,
  shirt,
  auction,
  bidStatus,
}: AuctionShirtCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { isLiked, toggle } = useFavorites();

  const id = shirt?.id ?? auction?.shirt_id ?? String(idx);
  const liked = isAuthed ? isLiked(id) : false;
  const [likeCount, setLikeCount] = useState(shirt?.likes_count ?? 0);
  useEffect(() => {
    if (shirt?.likes_count != null) setLikeCount(shirt.likes_count);
  }, [shirt?.id, shirt?.likes_count]);

  const handleClick = () => navigate(`/shirt/auction/${id}`);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthed) {
      openAuthModal();
      return;
    }
    toggle(id, true);
    setLikeCount((c) => (liked ? c - 1 : c + 1));
  };

  // Use real auction end_time if available, fallback to shirt, then hoursLeft prop
  const auctionEndTime = auction?.end_time ?? shirt?.auction_end_time ?? undefined;
  const { label: timeLeftFromAuction, urgent: auctionUrgent } = useTimeLeft(auctionEndTime);
  const isUrgent = auctionEndTime ? auctionUrgent : (hoursLeft ?? 0) < 24;
  const timeLabel = timeLeftFromAuction
    ? timeLeftFromAuction
    : hoursLeft >= 24
      ? t(
          Math.floor(hoursLeft / 24) === 1
            ? "auction.dayLeft"
            : "auction.daysLeft",
          { count: Math.floor(hoursLeft / 24) },
        )
      : t("auction.hoursLeft", { count: hoursLeft });

  const clubName = shirt?.club || "CLUB NAME";
  const playerName = shirt?.player || "PLAYER NAME";
  const bidPrice = auction?.current_price ?? shirt?.auction_starting_price ?? 1.00;
  const season = shirt?.season || "2000 – 2001";
  const size = shirt?.size ? `Maat ${shirt.size}` : "Maat L";

  const fallbackSrc: ImageUrl = {
    thumbnail: "/images/FSM_shirt-removebg-preview.png",
    original: "/images/FSM_shirt-removebg-preview.png",
    optimized: "/images/FSM_shirt-removebg-preview.png",
  }
  const imgSrc = useMemo(() => {
    const sorted = [...(shirt?.image_urls ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (sorted[0]) return sorted[0];
    return fallbackSrc;
  }, [shirt]);
  const [imgOk, setImgOk] = useState(true);

  return (
    <article
      className="shirt-card shirt-card--auction"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="shirt-badges">
        <span className="shirt-auction-badge">{t("auction.badge")}</span>
      </div>
      <div className="shirt-img-zone">
        <img
          className="shirt-illustration"
          src={imgOk ? imgSrc.thumbnail : fallbackSrc.thumbnail}
          alt={shirt ? `${clubName} shirt` : "Voetbalshirt"}
          loading="lazy"
          onError={() => setImgOk(false)}
        />
        {bidStatus && (
          <div className={`auction-bid-status auction-bid-status--${bidStatus}`}>
            {bidStatus === "leading"
              ? "✓ Hoogste bieder"
              : bidStatus === "outbid"
                ? "! Overboden !"
                : "···"}
          </div>
        )}

        <div className="shirt-img-bottom">
          <div className={`shirt-time-overlay${timeLabel === "Afgelopen" ? " shirt-time-overlay--ended" : isUrgent ? " shirt-time-overlay--urgent" : ""}`}>
            <svg className="shirt-time-overlay__icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="8" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <line x1="8" y1="9" x2="8" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="8" y1="9" x2="10.5" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="6.5" y1="2" x2="9.5" y2="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {timeLabel}
          </div>

          <div className="shirt-wishlist">
            <button
              className={`shirt-heart${liked ? " liked" : ""}`}
              aria-label={t("header.favorites")}
              onClick={handleLike}
            >
              <img
                src={
                  liked
                    ? "/images/full_heart_icon.svg"
                    : "/images/empty_heart_icon.svg"
                }
                alt=""
              />
            </button>
            <span className="shirt-like-count">{likeCount}</span>
          </div>
        </div>
      </div>

      <div className="shirt-info">
        <div className="shirt-top-row">
          <div className="shirt-text">
            <div className="shirt-club">{clubName}</div>
            <div className="shirt-player">{playerName}</div>
          </div>
          <div className="shirt-price-block">
            <span className="shirt-current-bid-label">
              {t("auction.currentBid")}
            </span>
            <div className="shirt-price">€{bidPrice}</div>
          </div>
        </div>
        <div className="shirt-pills">
          <span className="shirt-pill shirt-pill-year">{season}</span>
          <span className="shirt-pill shirt-pill-size">{size}</span>
        </div>
      </div>
    </article>
  );
}

const AuctionShirtCard = React.memo(AuctionShirtCardInner);
export default AuctionShirtCard;
